/**
 * Relation Config Menu - Column Header Configuration
 *
 * Styled to match AFFiNE's design system.
 * Config is ONLY accessible via Column Header Menu, NOT via cells.
 *
 * E013-S002: Extended to support cross-doc relations via DatabaseDiscoveryBridge.
 * E012-T018: Uses RBAC-filtered database lists for security.
 */
import { I18n } from '@affine/i18n';
import { SignalWatcher, WithDisposable } from '@blocksuite/global/lit';
import { ShadowlessElement } from '@blocksuite/std';
import type { Store } from '@blocksuite/store';
import { BRAND_DISPLAY_NAME } from '@streamlain/brand';
import { css, html } from 'lit';
import { property, state } from 'lit/decorators.js';

import {
  canWriteRelationEndpoint,
  getRelationRestrictedDatabaseTitle,
  resolveRelationEndpointAccess,
} from './access-state';
import { countMultiValueCells } from './database-discovery';
import {
  type DatabasesByDoc,
  getDatabasesByDoc,
  getWritableDatabasesByDoc,
  getWritableDatabasesByDocLiveData,
  type WorkspaceDatabaseInfo,
} from './database-discovery-bridge';
import type { RelationPropertyData } from './define';
import {
  canModifyTargetDoc,
  createInverseRelationUnified,
  deleteInverseRelationUnified,
  truncateCellsUnified,
  updateColumnCardinalityUnified,
  updateInverseCardinalityUnified,
} from './unified-relation-service';

function tRelation(
  key: string,
  fallback: string,
  args?: Record<string, string | number>
): string {
  const fallbackValue = args
    ? fallback.replace(/\{\{(\w+)\}\}/g, (match, name: string) =>
        args[name] === undefined ? match : String(args[name])
      )
    : fallback;
  const translate = (
    I18n as unknown as Record<
      string,
      (args?: Record<string, string | number>) => string
    >
  )[key];
  if (typeof translate !== 'function') {
    return fallbackValue;
  }
  const translated = translate(args);
  return translated && translated !== key ? translated : fallbackValue;
}

// Icons
const LinkIcon = html`<svg
  width="16"
  height="16"
  viewBox="0 0 16 16"
  fill="none"
  xmlns="http://www.w3.org/2000/svg"
>
  <path
    d="M6.5 9.5L9.5 6.5M7.5 4.5L8.25 3.75C9.49264 2.50736 11.5074 2.50736 12.75 3.75C13.9926 4.99264 13.9926 7.00736 12.75 8.25L12 9M4 7L3.25 7.75C2.00736 8.99264 2.00736 11.0074 3.25 12.25C4.49264 13.4926 6.50736 13.4926 7.75 12.25L8.5 11.5"
    stroke="currentColor"
    stroke-width="1.5"
    stroke-linecap="round"
    stroke-linejoin="round"
  />
</svg>`;

const CheckIcon = html`<svg
  width="16"
  height="16"
  viewBox="0 0 16 16"
  fill="none"
  xmlns="http://www.w3.org/2000/svg"
>
  <path
    d="M3.5 8.5L6.5 11.5L12.5 5.5"
    stroke="currentColor"
    stroke-width="1.5"
    stroke-linecap="round"
    stroke-linejoin="round"
  />
</svg>`;

export function createAvailableRelationColumnName(
  desiredName: string,
  existingColumnNames: readonly string[]
): string {
  const baseName = desiredName.trim() || 'Relation';
  const existing = new Set(existingColumnNames.map(name => name.trim()));

  if (!existing.has(baseName)) {
    return baseName;
  }

  let suffix = 2;
  while (existing.has(`${baseName} ${suffix}`)) {
    suffix += 1;
  }

  return `${baseName} ${suffix}`;
}

export class RelationConfigMenu extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  static override styles = css`
    .relation-config {
      padding: 8px;
      min-width: 260px;
      font-family: var(--streamlain-font-family);
    }

    .relation-config-header {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 4px 8px;
      margin-bottom: 8px;
      color: var(--streamlain-text-primary-color);
      font-size: 14px;
      font-weight: 500;
    }

    .relation-config-header svg {
      color: var(--streamlain-icon-color);
    }

    .relation-config-section {
      margin-bottom: 12px;
    }

    .relation-config-label {
      font-size: 12px;
      font-weight: 500;
      color: var(--streamlain-text-secondary-color);
      margin-bottom: 6px;
      padding: 0 4px;
      display: block;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .relation-config-select {
      width: 100%;
      padding: 8px 12px;
      border: 1px solid var(--streamlain-border-color);
      border-radius: 8px;
      background: var(--streamlain-background-primary-color);
      color: var(--streamlain-text-primary-color);
      font-size: 14px;
      font-family: var(--streamlain-font-family);
      cursor: pointer;
      transition:
        border-color 0.15s ease,
        box-shadow 0.15s ease;
      appearance: none;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23888' d='M3 4.5L6 7.5L9 4.5'/%3E%3C/svg%3E");
      background-repeat: no-repeat;
      background-position: right 12px center;
    }

    .relation-config-select:hover {
      border-color: var(--streamlain-primary-color);
    }

    .relation-config-select:focus {
      outline: none;
      border-color: var(--streamlain-primary-color);
      box-shadow: 0 0 0 2px var(--streamlain-primary-color-2);
    }

    .relation-config-option-group {
      display: flex;
      gap: 8px;
    }

    .relation-config-option {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      padding: 10px 12px;
      border: 1px solid var(--streamlain-border-color);
      border-radius: 8px;
      background: var(--streamlain-background-primary-color);
      color: var(--streamlain-text-primary-color);
      font-size: 13px;
      font-family: var(--streamlain-font-family);
      cursor: pointer;
      transition: all 0.15s ease;
      user-select: none;
    }

    .relation-config-option:hover {
      border-color: var(--streamlain-primary-color);
      background: var(--streamlain-hover-color);
    }

    .relation-config-option.selected {
      border-color: var(--streamlain-primary-color);
      background: var(--streamlain-primary-color-1);
      color: var(--streamlain-primary-color);
      font-weight: 500;
    }

    .relation-config-option input {
      display: none;
    }

    .relation-config-toggle {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 10px 12px;
      border: 1px solid var(--streamlain-border-color);
      border-radius: 8px;
      background: var(--streamlain-background-primary-color);
      cursor: pointer;
      transition: all 0.15s ease;
      user-select: none;
    }

    .relation-config-toggle:hover {
      border-color: var(--streamlain-primary-color);
      background: var(--streamlain-hover-color);
    }

    .relation-config-toggle.active {
      border-color: var(--streamlain-primary-color);
      background: var(--streamlain-primary-color-1);
    }

    .relation-config-toggle-label {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .relation-config-toggle-title {
      font-size: 14px;
      color: var(--streamlain-text-primary-color);
    }

    .relation-config-toggle-desc {
      font-size: 11px;
      color: var(--streamlain-text-secondary-color);
    }

    .relation-config-switch {
      width: 36px;
      height: 20px;
      border-radius: 10px;
      background: var(--streamlain-border-color);
      position: relative;
      transition: background 0.2s ease;
    }

    .relation-config-switch::after {
      content: '';
      position: absolute;
      top: 2px;
      left: 2px;
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background: white;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
      transition: transform 0.2s ease;
    }

    .relation-config-toggle.active .relation-config-switch {
      background: var(--streamlain-primary-color);
    }

    .relation-config-toggle.active .relation-config-switch::after {
      transform: translateX(16px);
    }

    .relation-config-toggle.disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    .relation-config-toggle.disabled:hover {
      border-color: var(--streamlain-border-color);
      background: var(--streamlain-background-primary-color);
    }

    .relation-config-input {
      width: 100%;
      padding: 8px 12px;
      border: 1px solid var(--streamlain-border-color);
      border-radius: 8px;
      background: var(--streamlain-background-primary-color);
      color: var(--streamlain-text-primary-color);
      font-size: 14px;
      font-family: var(--streamlain-font-family);
      margin-top: 8px;
      box-sizing: border-box;
      transition:
        border-color 0.15s ease,
        box-shadow 0.15s ease;
    }

    .relation-config-input:focus {
      outline: none;
      border-color: var(--streamlain-primary-color);
      box-shadow: 0 0 0 2px var(--streamlain-primary-color-2);
    }

    .relation-config-input::placeholder {
      color: var(--streamlain-placeholder-color);
    }

    .relation-config-hint {
      font-size: 11px;
      color: var(--streamlain-text-secondary-color);
      padding: 6px 4px 0;
      line-height: 1.4;
    }

    .relation-config-hint.success {
      color: var(--streamlain-success-color);
      display: flex;
      align-items: center;
      gap: 4px;
    }

    .relation-config-hint.success svg {
      width: 12px;
      height: 12px;
    }

    .relation-config-divider {
      height: 1px;
      background: var(--streamlain-border-color);
      margin: 12px 0;
    }

    /* Primary CTA — blue-white theme ships no --streamlain-v2-button-pureWhiteText
       (theme package only defines it for light/dark), so we hard-code #ffffff.
       Three-class specificity keeps scoped database viewport theme boundaries
       from overriding the CTA foreground. */
    .relation-config-save.relation-config-save.relation-config-save {
      width: 100%;
      padding: 10px 16px;
      background: var(--streamlain-primary-color);
      color: #ffffff;
      border: none;
      border-radius: 8px;
      cursor: pointer;
      font-size: 14px;
      font-weight: 500;
      font-family: var(--streamlain-font-family);
      transition:
        opacity 0.15s ease,
        transform 0.1s ease;
    }

    .relation-config-save.relation-config-save.relation-config-save:hover:not(
        :disabled
      ) {
      opacity: 0.9;
    }

    .relation-config-save.relation-config-save.relation-config-save:active:not(
        :disabled
      ) {
      transform: scale(0.98);
    }

    .relation-config-save.relation-config-save.relation-config-save:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }

    /* E013: Search input styles */
    .relation-config-search-container {
      margin-bottom: 8px;
    }

    .relation-config-search-input {
      width: 100%;
      padding: 8px 12px;
      border: 1px solid var(--streamlain-border-color);
      border-radius: 8px;
      background: var(--streamlain-background-primary-color);
      color: var(--streamlain-text-primary-color);
      font-size: 13px;
      font-family: var(--streamlain-font-family);
      box-sizing: border-box;
      transition:
        border-color 0.15s ease,
        box-shadow 0.15s ease;
    }

    .relation-config-search-input:focus {
      outline: none;
      border-color: var(--streamlain-primary-color);
      box-shadow: 0 0 0 2px var(--streamlain-primary-color-2);
    }

    .relation-config-search-input::placeholder {
      color: var(--streamlain-placeholder-color);
    }

    /* E013-S002: Grouped database selector styles */
    .relation-config-db-group {
      margin-bottom: 4px;
    }

    .relation-config-db-group-header {
      font-size: 12px;
      font-weight: 500;
      color: var(--streamlain-text-secondary-color);
      padding: 6px 8px;
      display: flex;
      align-items: center;
      gap: 6px;
    }

    .relation-config-db-group-header.current {
      color: var(--streamlain-primary-color);
    }

    .relation-config-current-badge {
      font-size: 10px;
      font-weight: 400;
      opacity: 0.7;
      padding: 1px 4px;
      border-radius: 3px;
      background: var(--streamlain-hover-color);
    }

    .relation-config-db-item {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 12px;
      border: 1px solid transparent;
      border-radius: 6px;
      cursor: pointer;
      transition: all 0.15s ease;
      margin-left: 8px;
    }

    .relation-config-db-item:hover {
      background: var(--streamlain-hover-color);
      border-color: var(--streamlain-border-color);
    }

    .relation-config-db-item.selected {
      background: var(--streamlain-primary-color-1);
      border-color: var(--streamlain-primary-color);
    }

    .relation-config-db-item-title {
      font-size: 13px;
      color: var(--streamlain-text-primary-color);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .relation-config-db-list {
      max-height: 240px;
      overflow-y: auto;
      border: 1px solid var(--streamlain-border-color);
      border-radius: 8px;
      padding: 4px;
    }

    .relation-config-db-list::-webkit-scrollbar {
      width: 6px;
    }

    .relation-config-db-list::-webkit-scrollbar-track {
      background: transparent;
    }

    .relation-config-db-list::-webkit-scrollbar-thumb {
      background: var(--streamlain-border-color);
      border-radius: 3px;
    }

    .relation-config-db-list::-webkit-scrollbar-thumb:hover {
      background: var(--streamlain-icon-color);
    }

    .relation-config-no-dbs {
      font-size: 12px;
      color: var(--streamlain-text-secondary-color);
      padding: 12px;
      text-align: center;
    }

    /* Dropdown Combobox Styles */
    .relation-config-dropdown {
      position: relative;
    }

    .relation-config-dropdown-trigger {
      width: 100%;
      padding: 8px 32px 8px 12px;
      border: 1px solid var(--streamlain-border-color);
      border-radius: 8px;
      background: var(--streamlain-background-primary-color);
      color: var(--streamlain-text-primary-color);
      font-size: 14px;
      font-family: var(--streamlain-font-family);
      cursor: pointer;
      transition:
        border-color 0.15s ease,
        box-shadow 0.15s ease;
      text-align: left;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      position: relative;
    }

    .relation-config-dropdown-trigger::after {
      content: '';
      position: absolute;
      right: 12px;
      top: 50%;
      transform: translateY(-50%);
      width: 0;
      height: 0;
      border-left: 5px solid transparent;
      border-right: 5px solid transparent;
      border-top: 5px solid var(--streamlain-icon-color);
      transition: transform 0.15s ease;
    }

    .relation-config-dropdown-trigger.open::after {
      transform: translateY(-50%) rotate(180deg);
    }

    .relation-config-dropdown-trigger:hover {
      border-color: var(--streamlain-primary-color);
    }

    .relation-config-dropdown-trigger.placeholder {
      color: var(--streamlain-placeholder-color);
    }

    /* E018-S008-T006: Locked state for configured relations */
    .relation-config-dropdown-trigger.locked {
      cursor: not-allowed;
      background: var(--streamlain-background-secondary-color);
      opacity: 0.8;
    }

    .relation-config-dropdown-trigger.locked::after {
      display: none;
    }

    .relation-config-locked-hint {
      font-size: 11px;
      color: var(--streamlain-text-secondary-color);
      padding: 4px 4px 0;
      font-style: italic;
    }

    .relation-config-dropdown-panel {
      position: absolute;
      top: 100%;
      left: 0;
      right: 0;
      margin-top: 4px;
      border: 1px solid var(--streamlain-border-color);
      border-radius: 8px;
      background: var(--streamlain-background-primary-color);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      z-index: 100;
      overflow: hidden;
    }

    .relation-config-dropdown-search {
      padding: 8px;
      border-bottom: 1px solid var(--streamlain-border-color);
    }

    .relation-config-dropdown-search input {
      width: 100%;
      padding: 6px 10px;
      border: 1px solid var(--streamlain-border-color);
      border-radius: 6px;
      background: var(--streamlain-background-secondary-color);
      color: var(--streamlain-text-primary-color);
      font-size: 13px;
      font-family: var(--streamlain-font-family);
      box-sizing: border-box;
    }

    .relation-config-dropdown-search input:focus {
      outline: none;
      border-color: var(--streamlain-primary-color);
    }

    .relation-config-dropdown-search input::placeholder {
      color: var(--streamlain-placeholder-color);
    }

    .relation-config-dropdown-list {
      max-height: 200px;
      overflow-y: auto;
      padding: 4px;
    }

    .relation-config-dropdown-list::-webkit-scrollbar {
      width: 6px;
    }

    .relation-config-dropdown-list::-webkit-scrollbar-track {
      background: transparent;
    }

    .relation-config-dropdown-list::-webkit-scrollbar-thumb {
      background: var(--streamlain-border-color);
      border-radius: 3px;
    }

    /* Delete button in config menu */
    .relation-config-delete-section {
      padding: 12px;
      border-top: 1px solid var(--streamlain-border-color);
    }

    .relation-config-delete-btn {
      width: 100%;
      padding: 8px 12px;
      border-radius: 8px;
      border: 1px solid var(--streamlain-error-color);
      background: transparent;
      color: var(--streamlain-error-color);
      cursor: pointer;
      font-size: 14px;
      transition: all 0.15s;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
    }

    .relation-config-delete-btn:hover {
      background: var(--streamlain-error-color);
      color: white;
    }
  `;

  @property({ attribute: false })
  accessor store!: Store;

  @property({ attribute: false })
  accessor currentDatabaseId!: string;

  @property({ attribute: false })
  accessor propertyId!: string;

  @property({ attribute: false })
  accessor data!: RelationPropertyData;

  @property({ attribute: false })
  accessor onUpdate!: (data: Partial<RelationPropertyData>) => void;

  @property({ attribute: false })
  accessor sourceColumnName = '';

  @property({ attribute: false })
  accessor sourceColumnNames: readonly string[] = [];

  @property({ attribute: false })
  accessor onPreviewSourceColumnName: ((name: string) => void) | undefined;

  @property({ attribute: false })
  accessor onCommitSourceColumnName: ((name: string) => void) | undefined;

  @property({ attribute: false })
  accessor onClose: (() => void) | undefined;

  /** E012-T016: Callback to delete the property */
  @property({ attribute: false })
  accessor onDelete: (() => void) | undefined;

  @state()
  accessor _targetDocId = ''; // E013: Doc ID for cross-doc relations

  @state()
  accessor _targetDatabaseId = '';

  @state()
  accessor _cardinality: 'one' | 'many' = 'many';

  @state()
  accessor _inverseCardinality: 'one' | 'many' = 'many';

  // Relations are always bidirectional — the user no longer has a choice.
  // Kept as a field (not removed) to minimize churn in handleSave / sync paths.
  @state()
  accessor _bidirectional = true;

  @state()
  accessor _backlinkName = 'Related Items';

  private _backlinkNameEdited = false;

  private _pendingSourceColumnName: string | undefined;

  @state()
  accessor _initialized = false;

  @state()
  accessor _searchQuery = '';

  @state()
  accessor _dropdownOpen = false;

  // E013: Subscription to bridge LiveData for reactive updates
  private _bridgeUnsubscribe: (() => void) | null = null;

  override connectedCallback() {
    super.connectedCallback();
    this._syncFromData();

    // E013: Subscribe to bridge LiveData for reactive database list updates.
    // Relation creation is bidirectional and therefore needs Doc_Update on the
    // target database doc, not just Doc_Read.
    const liveData$ = getWritableDatabasesByDocLiveData();
    if (liveData$) {
      const subscription = liveData$.subscribe(() => {
        this.requestUpdate();
      });
      this._bridgeUnsubscribe = () => subscription.unsubscribe();
    }
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    // Cleanup subscription
    if (this._bridgeUnsubscribe) {
      this._bridgeUnsubscribe();
      this._bridgeUnsubscribe = null;
    }
  }

  override updated(changedProperties: Map<string, unknown>) {
    super.updated(changedProperties);
    // Re-sync when data property changes
    if (changedProperties.has('data') && this.data) {
      this._syncFromData();
    }
  }

  private _syncFromData() {
    if (!this.data) return;
    this._targetDocId = this.data.targetDocId || ''; // E013: Sync docId
    this._targetDatabaseId = this.data.targetDatabaseId || '';
    this._cardinality = this.data.cardinality || 'many';
    this._inverseCardinality = this.data.inverseCardinality || 'many';
    // Always bidirectional — no uni-directional option anymore.
    this._bidirectional = true;
    this._backlinkNameEdited = false;

    this._initialized = true;
  }

  /**
   * E013-S002: Get current doc ID from store
   */
  private get currentDocId(): string {
    return this.store.id;
  }

  /**
   * E013-S002: Get databases grouped by doc.
   * Uses workspace-wide discovery if bridge is initialized,
   * falls back to local discovery otherwise.
   */
  /**
   * Handle wheel events to prevent scroll-through to canvas
   */
  private _handleWheel(e: WheelEvent) {
    e.stopPropagation();
  }

  private get databasesByDoc(): DatabasesByDoc[] {
    // E018-S008: Cross-Doc native - use workspace discovery only, no local fallback.
    // Relations are always bidirectional; target selection must be limited to
    // docs where the inverse column can be created/updated.
    const allByDoc = getWritableDatabasesByDoc();

    // Separate current doc from other docs (self-relations allowed)
    const currentDocGroup = allByDoc.find(g => g.docId === this.currentDocId);
    const otherDocGroups = allByDoc.filter(g => g.docId !== this.currentDocId);

    const result: DatabasesByDoc[] = [];

    // Add current doc first (including current database for self-relations)
    if (currentDocGroup && currentDocGroup.databases.length > 0) {
      result.push({
        docId: currentDocGroup.docId,
        docTitle: currentDocGroup.docTitle,
        databases: currentDocGroup.databases,
      });
    }

    // Add other docs
    result.push(...otherDocGroups);

    return result;
  }

  private get allDatabasesByDoc(): DatabasesByDoc[] {
    return getDatabasesByDoc();
  }

  /**
   * E013-S002: Handle database selection from grouped list
   */
  private _selectDatabase(db: WorkspaceDatabaseInfo) {
    this._targetDocId = db.docId;
    this._targetDatabaseId = db.blockId;
    this._dropdownOpen = false;
    this._searchQuery = '';

    if (!this.data?.targetDatabaseId && this._isSourceColumnDefaultName()) {
      const sourceName = this._createDefaultSourceColumnName(db);
      this.sourceColumnName = sourceName;
      this._pendingSourceColumnName = sourceName;
      this.onPreviewSourceColumnName?.(sourceName);
    }

    if (!this.data?.inverseFieldId && !this._backlinkNameEdited) {
      this._backlinkName = this._createDefaultBacklinkName(db);
    }
  }

  private _isSourceColumnDefaultName(): boolean {
    const name = this.sourceColumnName.trim();
    return !name || name === 'Relation';
  }

  private _createDefaultSourceColumnName(
    targetDb: WorkspaceDatabaseInfo
  ): string {
    return createAvailableRelationColumnName(
      targetDb.databaseTitle,
      this.sourceColumnNames
    );
  }

  private _createDefaultBacklinkName(targetDb: WorkspaceDatabaseInfo): string {
    const sourceDbTitle = this._getSourceDatabaseTitle();
    return createAvailableRelationColumnName(
      sourceDbTitle,
      targetDb.columns.map(column => column.name)
    );
  }

  private _getSourceDatabaseTitle(): string {
    for (const group of this.allDatabasesByDoc) {
      const db = group.databases.find(
        database => database.blockId === this.currentDatabaseId
      );
      if (db?.databaseTitle.trim()) {
        return db.databaseTitle.trim();
      }
    }

    return 'Relation';
  }

  /**
   * Get the selected database title for display
   */
  private get selectedDatabaseTitle(): string | null {
    if (!this._targetDatabaseId) return null;

    const access = resolveRelationEndpointAccess({
      targetDatabaseId: this._targetDatabaseId,
      targetDocId: this._targetDocId || this.data?.targetDocId,
    });
    if (access.state === 'restricted') {
      return getRelationRestrictedDatabaseTitle();
    }
    return access.databaseTitle || null;
  }

  /**
   * Toggle dropdown open/close
   */
  private _toggleDropdown() {
    this._dropdownOpen = !this._dropdownOpen;
    if (!this._dropdownOpen) {
      this._searchQuery = '';
    }
  }

  /**
   * E013: Filter databases by search query
   */
  private get filteredDatabasesByDoc(): DatabasesByDoc[] {
    const query = this._searchQuery.toLowerCase().trim();
    if (!query) {
      return this.databasesByDoc;
    }

    return this.databasesByDoc
      .map(group => ({
        ...group,
        databases: group.databases.filter(
          db =>
            db.databaseTitle.toLowerCase().includes(query) ||
            group.docTitle.toLowerCase().includes(query)
        ),
      }))
      .filter(group => group.databases.length > 0);
  }

  private _handleSearchInput(e: Event) {
    const input = e.target as HTMLInputElement;
    this._searchQuery = input.value;
  }

  /**
   * E012-T015: Handle save with Cross-Doc support.
   * E018-S008-T005: Simplified with unified relation API.
   */
  private async handleSave() {
    if (!this._targetDatabaseId) return;

    // Check if cardinality is changing from many→one
    const oldCardinality = this.data?.cardinality || 'many';
    const oldInverseCardinality = this.data?.inverseCardinality || 'many';
    const sourceChangingToOne =
      oldCardinality === 'many' && this._cardinality === 'one';
    const inverseChangingToOne =
      this._bidirectional &&
      oldInverseCardinality === 'many' &&
      this._inverseCardinality === 'one';
    const targetDocId = this._targetDocId || this.currentDocId;
    const inverseTargetIsCrossDoc = targetDocId !== this.currentDocId;

    if (inverseChangingToOne && inverseTargetIsCrossDoc) {
      await this.showConfirm(
        'Relation could not be saved',
        'Cross-document cardinality truncation is not supported yet. The relation was not changed.',
        'OK',
        'error'
      );
      return;
    }

    // Count affected cells
    let sourceCount = 0;
    let inverseCount = 0;

    if (sourceChangingToOne) {
      sourceCount = countMultiValueCells(
        this.store,
        this.currentDatabaseId,
        this.propertyId
      );
    }

    if (
      inverseChangingToOne &&
      this.data?.inverseFieldId &&
      !inverseTargetIsCrossDoc
    ) {
      // For InDoc, we can count directly
      // For CrossDoc, we'd need a cross-doc count function (TODO)
      inverseCount = countMultiValueCells(
        this.store,
        this._targetDatabaseId,
        this.data.inverseFieldId
      );
    }

    let pendingChange: 'source' | 'inverse' | 'both' | null = null;

    // Show warning if truncation needed
    if (sourceCount > 0 || inverseCount > 0) {
      const totalAffected = sourceCount + inverseCount;
      pendingChange =
        sourceCount > 0 && inverseCount > 0
          ? 'both'
          : sourceCount > 0
            ? 'source'
            : 'inverse';

      const confirmed = await this.showConfirm(
        tRelation(
          'com.streamlain.streamlain.relation.data-loss-warning-title',
          'Data Loss Warning'
        ),
        tRelation(
          'com.streamlain.streamlain.relation.data-loss-warning-description',
          'Changing cardinality from Many to One will truncate {{count}} cell(s) to keep only the first value. This action cannot be undone.',
          { count: totalAffected }
        ),
        tRelation(
          'com.streamlain.streamlain.relation.truncate-and-save',
          'Truncate & Save'
        ),
        'error'
      );

      if (!confirmed) return;
    }

    const preparedSave = await this.prepareSave();
    if (!preparedSave) return;

    if (pendingChange) {
      const truncated = await this.performCardinalityTruncation(
        pendingChange,
        preparedSave.inverseFieldId
      );
      if (!truncated) return;
    }

    this.commitSave(preparedSave.inverseFieldId);
  }

  /**
   * Perform all async save steps that can still fail before cell truncation
   * mutates local relation values.
   */
  private async prepareSave(): Promise<{ inverseFieldId?: string } | null> {
    let inverseFieldId = this.data?.inverseFieldId;
    const targetDocId = this._targetDocId || this.currentDocId;

    if (
      !canWriteRelationEndpoint({
        targetDatabaseId: this._targetDatabaseId,
        targetDocId,
      })
    ) {
      await this.showConfirm(
        tRelation(
          'com.streamlain.streamlain.relation.target-read-only-title',
          'Relation target is read-only'
        ),
        tRelation(
          'com.streamlain.streamlain.relation.target-read-only-description',
          'This relation cannot be saved because the target database is not writable. Bidirectional relations require write access to both database documents.'
        ),
        tRelation('com.streamlain.confirmModal.button.ok', 'OK')
      );
      return null;
    }

    // E018-S008-T005: Use unified API - no if/else for InDoc vs CrossDoc
    if (this._bidirectional && !inverseFieldId) {
      const result = await createInverseRelationUnified({
        store: this.store,
        sourceDocId: this.currentDocId,
        sourceDatabaseId: this.currentDatabaseId,
        targetDocId,
        targetDatabaseId: this._targetDatabaseId,
        backlinkName: this._backlinkName,
        sourceRelationId: this.propertyId,
        inverseCardinality: this._inverseCardinality,
        sourceCardinality: this._cardinality, // For symmetric stealing
      });

      if (result.success && result.inversePropertyId) {
        inverseFieldId = result.inversePropertyId;
      } else {
        const message =
          result.error?.message ?? 'The inverse relation could not be created.';
        console.error(
          '[RelationConfig] Failed to create inverse relation:',
          message
        );
        await this.showConfirm(
          'Relation could not be saved',
          message,
          'OK',
          'error'
        );
        return null;
      }
    }

    // Sync cardinality changes to inverse column for symmetric stealing
    if (inverseFieldId && this._bidirectional) {
      // 1. source.cardinality → inverse.inverseCardinality
      // When our cardinality changes, the inverse column's inverseCardinality must match
      const inverseCardinalityResult = await updateInverseCardinalityUnified({
        store: this.store,
        currentDocId: this.currentDocId,
        targetDocId,
        targetDatabaseId: this._targetDatabaseId,
        inverseFieldId,
        newInverseCardinality: this._cardinality,
      });
      if (!inverseCardinalityResult.success) {
        await this.showConfirm(
          'Relation could not be saved',
          inverseCardinalityResult.error?.message ??
            'The inverse relation could not be updated.',
          'OK',
          'error'
        );
        return null;
      }

      // 2. source.inverseCardinality → inverse.cardinality
      // When our inverseCardinality changes, the inverse column's cardinality must match
      const columnCardinalityResult = await updateColumnCardinalityUnified({
        store: this.store,
        currentDocId: this.currentDocId,
        targetDocId,
        targetDatabaseId: this._targetDatabaseId,
        inverseFieldId,
        newCardinality: this._inverseCardinality,
      });
      if (!columnCardinalityResult.success) {
        await this.showConfirm(
          'Relation could not be saved',
          columnCardinalityResult.error?.message ??
            'The inverse relation could not be updated.',
          'OK',
          'error'
        );
        return null;
      }
    }

    return { inverseFieldId };
  }

  private commitSave(inverseFieldId: string | undefined) {
    this.onUpdate({
      targetDocId: this._targetDocId || undefined,
      targetDatabaseId: this._targetDatabaseId,
      targetFieldId: 'title',
      cardinality: this._cardinality,
      inverseFieldId,
      inverseCardinality: this._bidirectional
        ? this._inverseCardinality
        : undefined,
    });
    if (this._pendingSourceColumnName) {
      this.onCommitSourceColumnName?.(this._pendingSourceColumnName);
      this._pendingSourceColumnName = undefined;
    }

    // Close the menu after saving
    this.onClose?.();
  }

  // ==========================================================================
  // Native ConfirmModal Bridge
  // ==========================================================================

  /**
   * Show AFFiNE's native ConfirmModal via the window bridge.
   * Returns true if user confirmed, false if cancelled.
   */
  private showConfirm(
    title: string,
    description: string,
    confirmText: string,
    variant: 'primary' | 'error' = 'primary'
  ): Promise<boolean> {
    const modal = window.__confirmModal;
    if (!modal) {
      // Without the native modal, destructive relation operations must not
      // continue silently.
      console.warn('[RelationConfig] __confirmModal bridge not available');
      return Promise.resolve(false);
    }

    return new Promise<boolean>(resolve => {
      modal.openConfirmModal({
        title,
        description,
        confirmText,
        cancelText: 'Cancel',
        confirmButtonOptions: { variant },
        onConfirm: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
  }

  // ==========================================================================
  // Cardinality Change Logic
  // ==========================================================================

  /**
   * Perform truncation after user confirmed cardinality change.
   */
  private async performCardinalityTruncation(
    pendingChange: 'source' | 'inverse' | 'both',
    inverseFieldId: string | undefined
  ): Promise<boolean> {
    const targetDocId = this._targetDocId || this.currentDocId;

    if (pendingChange === 'source' || pendingChange === 'both') {
      const sourceResult = await truncateCellsUnified({
        store: this.store,
        currentDocId: this.currentDocId,
        targetDocId: this.currentDocId,
        targetDatabaseId: this.currentDatabaseId,
        propertyId: this.propertyId,
        inverseCardinality: this._inverseCardinality,
        inverseDatabaseId: this._targetDatabaseId,
        inverseDocId: targetDocId,
        inverseFieldId,
      });
      if (!sourceResult.success) {
        await this.showConfirm(
          'Relation could not be saved',
          sourceResult.error?.message ??
            'Relation cardinality could not be updated safely.',
          'OK',
          'error'
        );
        return false;
      }
    }

    if (
      (pendingChange === 'inverse' || pendingChange === 'both') &&
      inverseFieldId
    ) {
      const inverseResult = await truncateCellsUnified({
        store: this.store,
        currentDocId: this.currentDocId,
        targetDocId,
        targetDatabaseId: this._targetDatabaseId,
        propertyId: inverseFieldId,
        inverseCardinality: this._cardinality,
        inverseDatabaseId: this.currentDatabaseId,
        inverseDocId: this.currentDocId,
        inverseFieldId: this.propertyId,
      });
      if (!inverseResult.success) {
        await this.showConfirm(
          'Relation could not be saved',
          inverseResult.error?.message ??
            'Relation cardinality could not be updated safely.',
          'OK',
          'error'
        );
        return false;
      }
    }

    return true;
  }

  // ==========================================================================
  // E012-T016: Delete Logic (uses native ConfirmModal)
  // ==========================================================================

  /**
   * Show delete confirmation via native AFFiNE ConfirmModal.
   */
  private async handleDeleteClick() {
    const hasBidirectional = !!this.data?.inverseFieldId;

    if (!hasBidirectional) {
      if (this.data?.targetDatabaseId) {
        await this.showConfirm(
          'Relation could not be deleted',
          'This relation cannot be deleted safely because inverse relation metadata is missing.',
          'OK',
          'error'
        );
        return;
      }
      this.onDelete?.();
      this.onClose?.();
      return;
    }

    const targetDocId = this.data?.targetDocId || this.currentDocId;
    const canModify = await canModifyTargetDoc(this.currentDocId, targetDocId);
    const targetDbTitle = this.selectedDatabaseTitle || 'target database';

    if (!canModify) {
      await this.showConfirm(
        'Delete Relation',
        `This relation has a backlink in "${targetDbTitle}". You don't have write access to the target document, so ${BRAND_DISPLAY_NAME} cannot delete both sides safely.`,
        'OK',
        'primary'
      );
      return;
    }

    // Has permission: Ask to delete both sides
    const deleteBoth = await this.showConfirm(
      'Delete Relation',
      `This relation has a backlink in "${targetDbTitle}". Delete both sides (this relation and its backlink)?`,
      'Delete Both',
      'error'
    );

    if (deleteBoth) {
      await this.deleteWithInverse();
    }
  }

  /**
   * Delete the relation and its inverse backlink.
   */
  private async deleteWithInverse() {
    if (this.data?.inverseFieldId) {
      const targetDocId = this.data.targetDocId || this.currentDocId;
      const result = await deleteInverseRelationUnified({
        store: this.store,
        currentDocId: this.currentDocId,
        targetDocId,
        targetDatabaseId: this.data.targetDatabaseId,
        inverseFieldId: this.data.inverseFieldId,
      });

      if (!result.success) {
        console.error(
          '[RelationConfig] Failed to delete inverse relation:',
          result.error?.message
        );
        return;
      }
    }

    this.onDelete?.();
    this.onClose?.();
  }

  override render() {
    const isValid = this._targetDatabaseId !== '';
    const hasBidirectional = !!this.data?.inverseFieldId;
    // E018-S008-T006: Lock target database once configured (has saved targetDatabaseId)
    const isTargetLocked = !!this.data?.targetDatabaseId;

    // Stop propagation to prevent menu from closing
    const stopProp = (e: Event) => e.stopPropagation();

    return html`
      <div class="relation-config" @click=${stopProp} @mousedown=${stopProp}>
        <div class="relation-config-header">
          ${LinkIcon}
          <span>Relation Settings</span>
        </div>

        <!-- Target Database - Dropdown with integrated search -->
        <!-- E018-S008-T006: Locked once configured to prevent chaos -->
        <div class="relation-config-section">
          <label class="relation-config-label">Target Database</label>
          <div class="relation-config-dropdown">
            <button
              type="button"
              class="relation-config-dropdown-trigger ${this._dropdownOpen
                ? 'open'
                : ''} ${!this.selectedDatabaseTitle
                ? 'placeholder'
                : ''} ${isTargetLocked ? 'locked' : ''}"
              @click=${() => !isTargetLocked && this._toggleDropdown()}
              @mousedown=${stopProp}
              ?disabled=${isTargetLocked}
            >
              ${this.selectedDatabaseTitle || 'Select a database...'}
            </button>
            ${isTargetLocked
              ? html`<div class="relation-config-locked-hint">
                  Target cannot be changed after configuration. Delete and
                  recreate to change target.
                </div>`
              : ''}
            ${this._dropdownOpen && !isTargetLocked
              ? html`
                  <div
                    class="relation-config-dropdown-panel"
                    @click=${stopProp}
                    @mousedown=${stopProp}
                  >
                    <div class="relation-config-dropdown-search">
                      <input
                        type="text"
                        placeholder="Search databases..."
                        .value=${this._searchQuery}
                        @input=${this._handleSearchInput}
                        @click=${stopProp}
                        @mousedown=${stopProp}
                      />
                    </div>
                    <div
                      class="relation-config-dropdown-list"
                      @wheel=${this._handleWheel}
                    >
                      ${this.filteredDatabasesByDoc.length === 0
                        ? html`<div class="relation-config-no-dbs">
                            ${this._searchQuery
                              ? 'No matching databases'
                              : 'No databases found'}
                          </div>`
                        : this.filteredDatabasesByDoc.map(
                            group => html`
                              <div class="relation-config-db-group">
                                <div
                                  class="relation-config-db-group-header ${group.docId ===
                                  this.currentDocId
                                    ? 'current'
                                    : ''}"
                                >
                                  ${group.docTitle}
                                  ${group.docId === this.currentDocId
                                    ? html`<span
                                        class="relation-config-current-badge"
                                        >current</span
                                      >`
                                    : ''}
                                </div>
                                ${group.databases.map(
                                  db => html`
                                    <div
                                      class="relation-config-db-item ${this
                                        ._targetDatabaseId === db.blockId
                                        ? 'selected'
                                        : ''}"
                                      @click=${() => this._selectDatabase(db)}
                                    >
                                      <span
                                        class="relation-config-db-item-title"
                                        >${db.databaseTitle}</span
                                      >
                                    </div>
                                  `
                                )}
                              </div>
                            `
                          )}
                    </div>
                  </div>
                `
              : ''}
          </div>
        </div>

        <!-- Link Type -->
        <div class="relation-config-section">
          <label class="relation-config-label">Link Type</label>
          <div class="relation-config-option-group">
            <label
              class="relation-config-option ${this._cardinality === 'many'
                ? 'selected'
                : ''}"
              @click=${() => (this._cardinality = 'many')}
            >
              <input
                type="radio"
                name="cardinality"
                value="many"
                ?checked=${this._cardinality === 'many'}
              />
              Many
            </label>
            <label
              class="relation-config-option ${this._cardinality === 'one'
                ? 'selected'
                : ''}"
              @click=${() => (this._cardinality = 'one')}
            >
              <input
                type="radio"
                name="cardinality"
                value="one"
                ?checked=${this._cardinality === 'one'}
              />
              One
            </label>
          </div>
          <div class="relation-config-hint">
            ${this._cardinality === 'many'
              ? 'Each row can link to multiple items'
              : 'Each row can link to only one item'}
          </div>
        </div>

        <div class="relation-config-divider"></div>

        <!-- Backlink config — relations are always bidirectional -->
        <div class="relation-config-section">
          ${!hasBidirectional
            ? html`
                <label class="relation-config-label"
                  >Backlink column name</label
                >
                <input
                  type="text"
                  class="relation-config-input"
                  placeholder="Backlink column name"
                  value=${this._backlinkName}
                  @input=${(e: Event) => {
                    this._backlinkNameEdited = true;
                    this._backlinkName = (e.target as HTMLInputElement).value;
                  }}
                  @click=${stopProp}
                  @mousedown=${stopProp}
                  @keydown=${stopProp}
                />

                <!-- Inverse Link Type -->
                <label class="relation-config-label" style="margin-top: 12px;"
                  >Backlink Type</label
                >
                <div class="relation-config-option-group">
                  <label
                    class="relation-config-option ${this._inverseCardinality ===
                    'many'
                      ? 'selected'
                      : ''}"
                    @click=${() => (this._inverseCardinality = 'many')}
                  >
                    <input
                      type="radio"
                      name="inverseCardinality"
                      value="many"
                      ?checked=${this._inverseCardinality === 'many'}
                    />
                    Many
                  </label>
                  <label
                    class="relation-config-option ${this._inverseCardinality ===
                    'one'
                      ? 'selected'
                      : ''}"
                    @click=${() => (this._inverseCardinality = 'one')}
                  >
                    <input
                      type="radio"
                      name="inverseCardinality"
                      value="one"
                      ?checked=${this._inverseCardinality === 'one'}
                    />
                    One
                  </label>
                </div>
                <div class="relation-config-hint">
                  ${this._inverseCardinality === 'many'
                    ? 'Target rows can link back to multiple source rows'
                    : 'Target rows can only link back to one source row'}
                </div>
              `
            : ''}
          ${hasBidirectional
            ? html`
                <div class="relation-config-hint success">
                  ${CheckIcon} Bidirectional link active
                </div>
              `
            : ''}
        </div>

        <!-- Save Button -->
        <button
          class="relation-config-save"
          ?disabled=${!isValid}
          @click=${() => this.handleSave()}
        >
          Save Configuration
        </button>

        <!-- E012-T016: Delete Button (only show if configured and onDelete provided) -->
        ${this.data?.targetDatabaseId && this.onDelete
          ? html`
              <div class="relation-config-delete-section">
                <button
                  class="relation-config-delete-btn"
                  @click=${() => this.handleDeleteClick()}
                >
                  Delete Relation Column
                </button>
              </div>
            `
          : ''}
      </div>
    `;
  }
}
